import api from '@/shared/utils/apiConfig';
import type { ApiResponse } from '@/shared/types/api';
import type { IncomeCategory } from '@/modules/income/api/income.api';

export interface IncomeCategoryListParams {
  includeInactive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface IncomeCategoryListResult {
  items: IncomeCategory[];
  total: number;
}

export interface CreateIncomeCategoryPayload {
  name: string;
  sortOrder?: number;
}

export interface UpdateIncomeCategoryPayload {
  name?: string;
  isActive?: boolean;
  sortOrder?: number;
}

const buildListParams = (params: IncomeCategoryListParams = {}) => ({
  includeInactive: params.includeInactive ? 'true' : undefined,
  search: params.search?.trim() || undefined,
  page: params.page,
  limit: params.limit,
});

export const incomeCategoryAPI = {
  listResult: async (
    params: IncomeCategoryListParams = {}
  ): Promise<IncomeCategoryListResult> => {
    const response = await api.get<ApiResponse<IncomeCategory[]>>('/income-categories', {
      params: buildListParams(params),
    });
    const items = response.data.data ?? [];
    return {
      items,
      total: response.data.total ?? items.length,
    };
  },

  getAll: async (includeInactive = false): Promise<ApiResponse<IncomeCategory[]>> => {
    const response = await api.get('/income-categories', {
      params: includeInactive ? { includeInactive: 'true' } : undefined
    });
    return response.data;
  },

  create: async (payload: CreateIncomeCategoryPayload): Promise<ApiResponse<IncomeCategory>> => {
    const response = await api.post('/income-categories', payload);
    return response.data;
  },

  update: async (id: string, payload: UpdateIncomeCategoryPayload): Promise<ApiResponse<IncomeCategory>> => {
    const response = await api.put(`/income-categories/${id}`, payload);
    return response.data;
  },

  deactivate: async (id: string): Promise<ApiResponse<IncomeCategory>> => {
    const response = await api.delete(`/income-categories/${id}`);
    return response.data;
  },
};
